import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import {
  MasterAgreementMappingRow,
  masterAgreementMappingsExportQueryOptions,
  useMasterAgreementMappings,
  useUpdateMasterAgreementMappings,
} from '@/hooks/queries/crt/useAgreementMappings'
import { useLastUpdatedAgreementMappingsLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { queryClient } from '@/main'
import { UpdateAgreementMappings } from '@repo/backend/crt'
import { CONTRACT_CLAUSES, ContractClause, INVESTMENT_TYPE, InvestmentType } from '@repo/constants'
import { Button } from '@repo/ui/components'
import { monthStartString } from '@repo/zod-schemas'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import InvestmentTypeMappingsDataTable from './-components/InvestmentTypeMappingsDataTable'
import MasterAgreementMappingsDataTable from './-components/MasterAgreementMappingsDataTable'
import { UploadMasterAgreementMappingsDataDialog } from './-components/UploadMasterAgreementMappingsDataDialog'

const UNIQUE_INVESTMENT_TYPES = [INVESTMENT_TYPE.GO] as const

const masterAgreementsSearchSchema = z.object({
  crtPeriod: monthStartString.optional(),
  validFrom: z.coerce.date().optional(),
  validTo: z.coerce.date().optional(),
})

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/data-sources/master-agreements/',
)({
  component: RouteComponent,
  validateSearch: (search) => masterAgreementsSearchSchema.parse(search),
})

function hasKEBudget(buckets: string[]): boolean {
  return buckets.some((b) => {
    const first = b.charAt(0).toUpperCase()
    return first === 'E' || first === 'K'
  })
}

// Order-insensitive comparison of two master-agreement selections (used to detect whether the GO
// mapping has actually changed from its saved value).
function areSameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every((value) => setB.has(value))
}

// One entry per master agreement whose mapping changed: newly selected ones get `investmentType`, newly
// cleared ones get `null`. Entries are drawn only from `original`/`selection`, so a partial view can
// only ever add or clear rows it displayed.
function diffInvestmentTypeSelection(
  original: string[],
  selection: string[],
  investmentType: typeof INVESTMENT_TYPE.GO,
): UpdateAgreementMappings['updatesByInvestmentType'] {
  const originalSet = new Set(original)
  const selectionSet = new Set(selection)
  return [
    ...selection
      .filter((masterAgreement) => !originalSet.has(masterAgreement))
      .map((masterAgreement) => ({ masterAgreement, investmentType })),
    ...original
      .filter((masterAgreement) => !selectionSet.has(masterAgreement))
      .map((masterAgreement) => ({ masterAgreement, investmentType: null })),
  ]
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { crtPeriod, validFrom, validTo } = Route.useSearch()

  const [keUpdates, setKeUpdates] = useState<Map<string, ContractClause | null>>(new Map())
  const [uniqueInvestmentTypesUpdates, setUniqueInvestmentTypesUpdates] = useState<
    Map<InvestmentType, string[]>
  >(new Map())

  // Key account worklist: honours the filter panel (CRT period + validity), resolved server-side.
  const {
    data: keData,
    isPending: isKePending,
    error: keError,
  } = useMasterAgreementMappings({ crtPeriod, validFrom, validTo })
  // GO eligibility does not depend on a period or a validity window, so this table always reads the full
  // universe of master agreements — the panel filters must never narrow what can be mapped to GO.
  const { data: goData, isPending: isGoPending, error: goError } = useMasterAgreementMappings()
  const { data: lastUpdatedTrail } = useLastUpdatedAgreementMappingsLog()

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTrail)

  const { mutate: updateMappings, isPending: isSaving } = useUpdateMasterAgreementMappings()

  // A master agreement belongs to the Key Account table when it has a K&E budget bucket, or when it
  // already carries a contract clause (e.g. an orphaned mapping whose agreement no longer exists but
  // whose saved value must stay visible and editable).
  const keRows: MasterAgreementMappingRow[] = (keData?.results ?? []).filter(
    (r) => hasKEBudget(r.buckets) || r.contractClause !== null,
  )
  // Everything outside the K and E buckets can be mapped to GO.
  const goRows: MasterAgreementMappingRow[] = goData?.results ?? []
  const goEligibleRows = goRows.filter((r) => !hasKEBudget(r.buckets))

  const handleKeValueChange = (masterAgreement: string, contractClause: ContractClause | null) => {
    setKeUpdates((prev) => {
      const next = new Map(prev)
      const original = keRows.find((r) => r.masterAgreement === masterAgreement)
      if ((original?.contractClause ?? null) === contractClause) {
        next.delete(masterAgreement)
      } else {
        next.set(masterAgreement, contractClause)
      }
      return next
    })
  }

  // Baseline drawn from the unfiltered rows: a filtered view must not look like the user cleared the GO
  // mappings it simply was not showing.
  const savedGoMasterAgreements = (investmentType: InvestmentType) =>
    goRows.filter((r) => r.investmentType === investmentType).map((r) => r.masterAgreement)

  const handleUniqueInvestmentTypeValueChange = (
    investmentType: InvestmentType,
    masterAgreements: string[],
  ) => {
    setUniqueInvestmentTypesUpdates((prev) => {
      const next = new Map(prev)
      if (areSameSet(masterAgreements, savedGoMasterAgreements(investmentType))) {
        next.delete(investmentType)
      } else {
        next.set(investmentType, masterAgreements)
      }
      return next
    })
  }

  const handleKeBulkEdit = (masterAgreements: string[], contractClause: ContractClause | null) => {
    setKeUpdates((prev) => {
      const next = new Map(prev)
      for (const masterAgreement of masterAgreements) {
        const original = keRows.find((r) => r.masterAgreement === masterAgreement)
        if ((original?.contractClause ?? null) === contractClause) {
          next.delete(masterAgreement)
        } else {
          next.set(masterAgreement, contractClause)
        }
      }
      return next
    })
  }

  const handleSaveChanges = () => {
    if (keUpdates.size === 0 && uniqueInvestmentTypesUpdates.size === 0) return

    const updatesByMasterAgreement: UpdateAgreementMappings['updatesByMasterAgreement'] = []
    keUpdates.forEach((contractClause, masterAgreement) => {
      updatesByMasterAgreement.push({ masterAgreement, contractClause })
    })

    const selection = uniqueInvestmentTypesUpdates.get(INVESTMENT_TYPE.GO)
    const updatesByInvestmentType: UpdateAgreementMappings['updatesByInvestmentType'] = selection
      ? diffInvestmentTypeSelection(
          savedGoMasterAgreements(INVESTMENT_TYPE.GO),
          selection,
          INVESTMENT_TYPE.GO,
        )
      : []

    updateMappings(
      { updatesByMasterAgreement, updatesByInvestmentType },
      {
        onSuccess: () => {
          setKeUpdates(new Map())
          setUniqueInvestmentTypesUpdates(new Map())
        },
      },
    )
  }

  const areChangesPending = keUpdates.size > 0 || uniqueInvestmentTypesUpdates.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread>
        <PageHeader>{t('masterAgreements.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={() =>
              queryClient.ensureQueryData(masterAgreementMappingsExportQueryOptions)
            }
            rowCount={keRows.length}
          />
          <PermissionsGuard required={['crt_agreement_mappings_edit']}>
            <UploadMasterAgreementMappingsDataDialog />
          </PermissionsGuard>
          <Button variant="secondary" asChild>
            <Link to="/crt/customer-investments/key-accounts">
              <ArrowLeftIcon className="size-4" />
              {t('customerInvestments.actions.backToKeyAccounts')}
            </Link>
          </Button>
        </HStack>
      </Spread>

      <MasterAgreementMappingsDataTable
        title={t('masterAgreements.keMasterAgreementsTitle')}
        data={keRows}
        optionSet={CONTRACT_CLAUSES}
        isPending={isKePending}
        error={keError}
        edited={keUpdates}
        onValueChange={handleKeValueChange}
        onBulkEdit={handleKeBulkEdit}
      />

      <InvestmentTypeMappingsDataTable
        title={t('masterAgreements.otherInvestmentTypesTitle')}
        allRows={goEligibleRows}
        investmentTypes={UNIQUE_INVESTMENT_TYPES}
        editedMappings={uniqueInvestmentTypesUpdates}
        onChange={handleUniqueInvestmentTypeValueChange}
        isPending={isGoPending}
        error={goError}
      />

      <PermissionsGuard required={['crt_agreement_mappings_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          lastUpdatedLog={lastUpdatedLog}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
